import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import ScButton from "@/src/components/ui-kit/button";
import { useAuth } from "@/src/contexts/auth.context";
import { ResolvedOrg } from "@/src/models/permissions.model";
import {
  getRefreshToken,
  getToken,
  useLogoutMutation,
} from "@/src/services/api/auth.api";
import { useGetOrganizationColorQuery } from "@/src/services/organization/organization.hooks";
import {
  useOrganizationsQuery,
  useSwitchOrgMutation,
} from "@/src/services/uam/permissions.blocks.api";
import { useTheme } from "@/src/styles/theme-provider";
import { derivePalette } from "@/src/utils/derive-palette";
import { useLocalSearchParams, useRouter } from "expo-router";
import { LockCircle } from "iconsax-react-nativejs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, Platform, Pressable, Text, View } from "react-native";
import Toast from "react-native-toast-message";

function OrgSelectSkeleton() {
  return (
    <View className="flex-1 bg-obsidian items-center justify-center p-6">
      <ActivityIndicator size="large" />
    </View>
  );
}

export default function OrgSelectScreen() {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { user, refreshAuth, logout } = useAuth();
  const router = useRouter();
  const { setPrimaryPalette } = useTheme();

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { mutateAsync: logoutMutateAsync } = useLogoutMutation();
  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      const refreshToken = await getRefreshToken();
      const accessToken = await getToken();

      if (refreshToken && accessToken) {
        await logoutMutateAsync({ refreshToken, accessToken });
      }
    } catch {
      // proceed with local logout even if the API call fails
    } finally {
      await logout();
      setIsLoggingOut(false);
    }
  };

  const handleConfirmLogout = async () => {
    setShowLogoutModal(false);
    await handleLogout();
  };

  const { mutateAsync: switchOrg, isPending: isSwitchOrgPending } =
    useSwitchOrgMutation();
  const { data: organizationsData, isLoading: isOrgsLoading } =
    useOrganizationsQuery();

  const memberships = user?.memberships ?? [];

  const resolvedOrgs: ReadonlyArray<ResolvedOrg> = memberships
    .map((membership) => {
      const org = organizationsData?.find(
        (o) => o.itemId === membership.organizationId,
      );

      const role = membership?.roles?.[0];

      if (!org || !role || membership.organizationId === "default") return null;

      return {
        organizationId: membership.organizationId,
        orgName: org.name,
        role: role
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
      };
    })
    .filter((org): org is ResolvedOrg => org !== null);

  // In your component body (outside the handler)
  const { data: orgColor, refetch: refetchOrgColor } =
    useGetOrganizationColorQuery({ enabled: false });

  // First function: handles org switching and auth refresh
  const handleSelectOrg = async (orgId: string) => {
    try {
      const refreshToken = (await getRefreshToken()) ?? "";
      if (Platform.OS !== "web" && !refreshToken)
        throw new Error("Missing refresh token");

      await switchOrg({ orgId, refreshToken });

      await refreshAuth(true);

      // Call second function to handle theme and navigation
      await handleOrgThemeAndNavigation();
    } catch {
      Toast.show({ type: "error", text1: t("ERROR_SWITCH_ORG_FAILED") });
    }
  };

  // Second function: handles theme retrieval, palette setting, and navigation
  const handleOrgThemeAndNavigation = async () => {
    const { data: newOrgColor } = await refetchOrgColor();

    if (newOrgColor?.primaryColor) {
      try {
        const primaryColorString = String(newOrgColor.primaryColor).trim();
        if (primaryColorString) {
          setPrimaryPalette(derivePalette(primaryColorString));
        } else {
          console.warn("Primary color is empty after conversion");
        }
      } catch (e) {
        console.error("Failed to derive palette from color:", e);
      }
    } else {
      console.warn("No primaryColor found in organization data");
    }

    const hasName = Boolean(
      user?.firstName?.trim() || user?.lastName?.trim(),
    );
    if (!hasName) {
      router.replace(`/profile/${user?.itemId}/edit?onboarding=true`);
    } else {
      router.replace("/feeds");
    }
  };
  const isLoading = isOrgsLoading || isSwitchOrgPending;

  if (isLoading) return <OrgSelectSkeleton />;

  const { pending } = useLocalSearchParams<{ pending?: string }>();

  if (pending === "true") {
    return (
      <>
        <View className="flex-1 bg-obsidian items-center justify-center gap-4 p-8">
          <LockCircle size={64} color={"var(--color-ash-1)"} />
          <Text className="text-xl font-semibold text-center text-onyx">
            {t("LABEL_PENDING_ORG_ASSIGNMENT")}
          </Text>
          <Text className="text-slate text-center text-sm">
            {t("LABEL_PENDING_ORG_ASSIGNMENT_DESC")}
          </Text>
          <ScButton
            text="BTN_LOGOUT"
            mode="outlined"
            onPress={() => {
              setShowLogoutModal(true);
            }}
          />
        </View>
        <ConfirmModal
          visible={showLogoutModal}
          title="LABEL_LOGOUT_CONFIRM"
          description="HINT_LOGOUT_CONFIRM"
          confirmText={t("BTN_LOGOUT")}
          cancelText={t("BTN_CANCEL")}
          loading={isLoggingOut}
          onCancel={() => setShowLogoutModal(false)}
          onConfirm={handleConfirmLogout}
        />
      </>
    );
  }

  return (
    <View className="flex-1 bg-obsidian items-center justify-center p-6">
      <Text className="text-2xl font-bold text-onyx mb-2">
        {t("LABEL_SELECT_ORGANIZATION")}
      </Text>
      <Text className="text-sm text-slate mb-8 text-center">
        {t("HINT_SELECT_ORGANIZATION")}
      </Text>

      <View className="w-full max-w-sm gap-3">
        {resolvedOrgs.map((org) => (
          <Pressable
            key={org.organizationId}
            onPress={() => handleSelectOrg(org.organizationId)}
            disabled={isSwitchOrgPending}
            className="bg-chalk border border-ash-3 rounded-xl p-4 active:opacity-80 hover:bg-ash-3"
          >
            <Text className="font-semibold text-onyx text-base">
              {org?.orgName}
            </Text>
            <Text className="text-xs text-slate mt-0.5">{org?.role}</Text>
          </Pressable>
        ))}
      </View>

      <ConfirmModal
        visible={showLogoutModal}
        title="LABEL_LOGOUT_CONFIRM"
        description="HINT_LOGOUT_CONFIRM"
        confirmText={t("BTN_LOGOUT")}
        cancelText={t("BTN_CANCEL")}
        loading={isLoggingOut}
        onCancel={() => setShowLogoutModal(false)}
        onConfirm={handleConfirmLogout}
      />
    </View>
  );
}
